import React, { useEffect, useState } from 'react';
import { isGovernmentPayorOrder } from '../util/gearOrderUtil';
import MedicareAdvantagePopover from './MedicareAdvantagePopover';
import { isPractitionerSupportsMedicare } from '../util/MedicareUtil';
import EditIcon from '../confirm/EditIcon';

/**
 * Displays the "Is this order for a Medicare or Tricare patient?" option. The option is only displayd for myGDX accounts.
 * After a choice is selected, the component condenses and only shows the question, answer, and edit button.
 *
 * @param gearOrder The current order object
 * @param onChange The function to call when the selection changes
 * @param disabled boolean indicating if the options should be disabled
 * @param userInfo The user
 * @param medicareOrder boolean (or null) Medicare? value
 * @param validationMessage An error message to show if the selection is not valid
 */
export default function MedicareChoice({ gearOrder, onChange, disabled, userInfo, medicareOrder, validationMessage }) {
	// Enable editing mode when the medicareOrder value is not set
	const [isEditing, setIsEditing] = useState(medicareOrder === null || medicareOrder === undefined);

	// Turn on editing mode if the medicareOrder value somehow gets unset
	useEffect(() => {
		if ((medicareOrder === null || medicareOrder === undefined) && !disabled) {
			setIsEditing(true);
		}
	}, [medicareOrder, disabled]);

	if (userInfo.type !== 'myGDX') {
		return null;
	}

	/**
	 * Calls the onChange method to toggle the Medicare order value.
	 *
	 * @param e the DOM event
	 */
	const toggleMedicareOrder = (e) => {
		onChange(e.currentTarget.value === 'true');
		disableEditing();
	};

	const enableEditing = () => {
		setIsEditing(true);
	};

	const disableEditing = () => {
		setIsEditing(false);
	};

	// Selection is disabled if any req has been accessioned
	const disableInputs = disabled || gearOrder?.requisitions?.some((req) => req.accessionNo);

	// Show a warning if an existing order is being switched to/from Medicare
	let medicareChangeNotice;
	if (
		gearOrder?.id && gearOrder?.requisitions?.length &&
		!!isGovernmentPayorOrder(gearOrder) !== !!medicareOrder &&
		(isPractitionerSupportsMedicare(gearOrder?.practitioner) || !medicareOrder)
	) {
		let originalMethod = 'Medicare';
		let newMethod = 'Non-Medicare';
		if (medicareOrder) {
			originalMethod = 'Non-Medicare';
			newMethod = 'Medicare';
		}

		medicareChangeNotice = (
			<div className="small">
				Items on this order have previously been saved with {originalMethod} billing. Continuing will update all
				items to {newMethod} billing.
			</div>
		);
	}

	return (
		<div className={'checkbox' + (disableInputs ? ' disabled' : '') + (validationMessage ? ' has-error' : '')}>
			<QuestionLabel isEditing={isEditing && !disableInputs} />
			{isEditing && !disableInputs ? (
				<EditForm
					medicareOrder={medicareOrder}
					toggleMedicareOrder={toggleMedicareOrder}
					disableEditing={disableEditing}
					disableInputs={disableInputs}
					validationMessage={validationMessage}
				/>
			) : (
				<Answer medicareOrder={medicareOrder} enableEditing={!disableInputs && enableEditing} />
			)}

			{medicareChangeNotice}
		</div>
	);
}

/**
 * Displays the Medicare question.
 * A "required" asterisk and Medicare Advantange text are displayed when in editing mode.
 */
const QuestionLabel = ({ isEditing }) => {
	return (
		<label style={{ marginRight: '10px', marginBottom: '2px', paddingLeft: '0', cursor: 'default' }}>
			<strong>
				Is this order for a Medicare or Tricare patient?{isEditing && <span className="text-danger">*</span>}
			</strong>
			{isEditing && (
				<>
					<br />
					(If the patient has a <MedicareAdvantagePopover />, select No)
				</>
			)}
		</label>
	);
};

/**
 * Displays the condensed answer.
 * An edit icon will be displayed if editing is allowed (inputs are not disabled)
 */
const Answer = ({ medicareOrder, enableEditing }) => {
	return (
		<>
			<span>{medicareOrder ? 'Yes' : 'No'} </span>
			{enableEditing && <EditIcon title="Medicare selection" onClick={enableEditing} />}
		</>
	);
};

/**
 * The form to change the Medicare choice
 */
const EditForm = ({ medicareOrder, toggleMedicareOrder, disableEditing, disableInputs, validationMessage }) => {
	return (
		<div>
			<label className="radio-inline">
				<input
					type="radio"
					name="medicare"
					value={true}
					checked={medicareOrder === true}
					onChange={toggleMedicareOrder}
					onClick={disableEditing}
					disabled={disableInputs}
				/>
				Yes
			</label>
			<label className="radio-inline">
				<input
					type="radio"
					name="medicare"
					value={false}
					checked={medicareOrder === false}
					onChange={toggleMedicareOrder}
					onClick={disableEditing}
					disabled={disableInputs}
				/>
				No / Unknown
			</label>
			{validationMessage && <div className="validation-message text-danger">{validationMessage}</div>}

			<div>
				<a
					href="/files/medicare/Notification-to-Physicians-Regarding-Medicare-Tests.pdf"
					target="_blank"
					title="Notification to Physicians Regarding Medicare Tests"
				>
					<span className="fa fa-external-link pe-1"></span>
					View Important Information Regarding Medicare
				</a>
			</div>
		</div>
	);
};
